/**
 * MediaPickerModal — Reusable media picker from the multimedia library.
 *
 * Props:
 *   open              {boolean}
 *   onClose           {function}
 *   onSelect          {function(url)}        — single-select (banner / single video)
 *   onSelectMultiple  {function(urls[])}     — multi-select images (photos tab)
 *   onSelectVideo     {function({title, video_url, thumbnail_url})} — pick video
 *   mediaType         'image' | 'video'      — default 'image'; if 'video' opens video tab directly
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, FolderOpen, ArrowLeft, Search, CheckCircle2,
  Loader2, ChevronRight, CheckSquare, Square, LayoutGrid,
  Play, Image as ImageIcon,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';
const STORAGE_BASE = API_BASE.replace(/\/api$/, '');

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${STORAGE_BASE}${url}`;
};

const ytThumb = (url) => {
  try {
    const u = new URL(url);
    const vid = u.searchParams.get('v') || (u.hostname === 'youtu.be' ? u.pathname.slice(1) : null);
    if (vid) return `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
  } catch {}
  return null;
};

export default function MediaPickerModal({
  open, onClose,
  onSelect, onSelectMultiple,
  onSelectVideo,
  mediaType = 'image',
}) {
  const multiMode = !!onSelectMultiple;
  const videoPickerMode = !!onSelectVideo || mediaType === 'video';

  const [tab, setTab] = useState(mediaType === 'video' ? 'videos' : 'images'); // 'images' | 'videos'
  const [view, setView] = useState('folders'); // for images: 'folders' | 'images'
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [loadingFolder, setLoadingFolder] = useState(null);
  const [search, setSearch] = useState('');
  const [checkedUrls, setCheckedUrls] = useState(new Set());

  useEffect(() => {
    if (!open) return;
    const initialTab = mediaType === 'video' ? 'videos' : 'images';
    setTab(initialTab);
    setView('folders');
    setActiveFolder(null);
    setImages([]);
    setSearch('');
    setCheckedUrls(new Set());
    if (initialTab === 'images') loadFolders();
    else loadVideos();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setView('folders');
    setActiveFolder(null);
    setImages([]);
    setCheckedUrls(new Set());
    if (tab === 'images') { if (folders.length === 0) loadFolders(); }
    else loadVideos();
  }, [tab]);

  const loadFolders = async () => {
    setLoadingFolders(true);
    try {
      const res = await fetch(`${API_BASE}/media?type=folder&limit=100`);
      const json = await res.json();
      if (json.success) setFolders(json.data || []);
    } catch {}
    finally { setLoadingFolders(false); }
  };

  const loadVideos = async () => {
    setLoadingVideos(true);
    try {
      const res = await fetch(`${API_BASE}/media?type=video&limit=200`);
      const json = await res.json();
      if (json.success) setVideos(json.data || []);
    } catch {}
    finally { setLoadingVideos(false); }
  };

  const fetchFolderImages = async (folderId) => {
    const res = await fetch(`${API_BASE}/folders/${folderId}/images`);
    const json = await res.json();
    return json.success ? (json.data?.images || []) : [];
  };

  const openFolder = async (folder) => {
    setActiveFolder(folder);
    setView('images');
    setSearch('');
    setCheckedUrls(new Set());
    setLoadingImages(true);
    try {
      const imgs = await fetchFolderImages(folder.id);
      setImages(imgs);
    } catch {}
    finally { setLoadingImages(false); }
  };

  // Bulk-select entire folder without drilling in
  const selectWholeFolder = async (folder, e) => {
    e.stopPropagation();
    if (!multiMode) return;
    setLoadingFolder(folder.id);
    try {
      const imgs = await fetchFolderImages(folder.id);
      const urls = imgs.map(i => i.image_url).filter(Boolean);
      if (urls.length) {
        onSelectMultiple(urls);
        onClose();
      }
    } catch {}
    finally { setLoadingFolder(null); }
  };

  // Single select (banner mode)
  const handleSingleSelect = (imgUrl) => {
    onSelect(resolveImg(imgUrl));
    onClose();
  };

  // Toggle checkbox
  const toggleCheck = (imgUrl) => {
    const raw = imgUrl; // keep raw url, resolve later
    setCheckedUrls(prev => {
      const next = new Set(prev);
      next.has(raw) ? next.delete(raw) : next.add(raw);
      return next;
    });
  };

  const toggleAll = () => {
    const allUrls = filteredImages.map(i => i.image_url).filter(Boolean);
    if (checkedUrls.size === allUrls.length) {
      setCheckedUrls(new Set());
    } else {
      setCheckedUrls(new Set(allUrls));
    }
  };

  const confirmMultiSelect = () => {
    if (!checkedUrls.size) return;
    onSelectMultiple([...checkedUrls]);
    onClose();
  };

  const handleVideoSelect = (video) => {
    if (onSelectVideo) {
      onSelectVideo({ title: video.name, video_url: video.video_url, thumbnail_url: video.thumbnail_url || null });
    } else if (onSelect) {
      onSelect(video.video_url);
    }
    onClose();
  };

  const filteredFolders = folders.filter(f => !search || f.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredImages = images.filter(img => !search || img.name?.toLowerCase().includes(search.toLowerCase()));
  const filteredVideos = videos.filter(v => !search || v.name?.toLowerCase().includes(search.toLowerCase()));
  const allChecked = filteredImages.length > 0 && filteredImages.every(i => checkedUrls.has(i.image_url));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl flex flex-col overflow-hidden"
            style={{ height: '82vh', maxHeight: '740px' }}
            initial={{ scale: 0.92, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
            onClick={e => e.stopPropagation()}
          >
            {/* ── Header ── */}
            <div className="flex items-center gap-3 px-5 py-4 border-b bg-white" style={{ fontFamily: 'Prompt' }}>
              {tab === 'images' && view === 'images' && (
                <button onClick={() => { setView('folders'); setCheckedUrls(new Set()); }}
                  className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition flex-shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                </button>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-bold text-gray-800 truncate">
                  {tab === 'videos'
                    ? 'เลือกวิดีโอจาก Multimedia Library'
                    : view === 'folders' ? 'เลือกรูปจาก Multimedia Library' : activeFolder?.name}
                </h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {tab === 'images' && view === 'images' && `${filteredImages.length} รูป`}
                  {tab === 'videos' && `${filteredVideos.length} วิดีโอ`}
                </p>
              </div>
              <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 transition flex-shrink-0">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* ── Tabs (image / video) ── */}
            <div className="flex border-b bg-white px-5" style={{ fontFamily: 'Prompt' }}>
              <button onClick={() => setTab('images')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                  tab === 'images' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <ImageIcon className="w-4 h-4" /> รูปภาพ
              </button>
              <button onClick={() => setTab('videos')}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition ${
                  tab === 'videos' ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}>
                <Play className="w-4 h-4" /> วิดีโอ
              </button>
            </div>

            {/* ── Search + toolbar ── */}
            <div className="px-5 py-3 border-b bg-gray-50 flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder={
                    tab === 'videos' ? 'ค้นหาวิดีโอ...' :
                    view === 'folders' ? 'ค้นหาโฟลเดอร์...' : 'ค้นหารูปภาพ...'
                  }
                  className="pl-9 h-9 text-sm" style={{ fontFamily: 'Prompt' }}
                />
              </div>
              {tab === 'images' && view === 'images' && multiMode && filteredImages.length > 0 && (
                <button onClick={toggleAll}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-purple-400 text-purple-600 rounded-lg hover:bg-purple-50 transition whitespace-nowrap"
                  style={{ fontFamily: 'Prompt' }}>
                  {allChecked
                    ? <><CheckSquare className="w-4 h-4" /> ยกเลิกทั้งหมด</>
                    : <><Square className="w-4 h-4" /> เลือกทั้งหมด ({filteredImages.length})</>
                  }
                </button>
              )}
            </div>

            {/* ── Content ── */}
            <div className="flex-1 overflow-y-auto p-4">

              {/* ── Videos tab ── */}
              {tab === 'videos' && (
                loadingVideos
                  ? <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
                  : filteredVideos.length === 0
                    ? <div className="text-center py-16 text-gray-400" style={{ fontFamily: 'Prompt' }}>
                        <Play className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p>ไม่พบวิดีโอ</p>
                      </div>
                    : <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {filteredVideos.map(video => {
                          const thumb = resolveImg(video.thumbnail_url) || ytThumb(video.video_url);
                          return (
                            <motion.button key={video.id}
                              onClick={() => handleVideoSelect(video)}
                              className="flex gap-3 items-start p-3 rounded-xl border border-gray-200 hover:border-purple-400 hover:shadow-md transition-all text-left bg-white group"
                              whileHover={{ y: -1, transition: { duration: 0.15 } }}
                            >
                              <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-900 relative">
                                {thumb
                                  ? <img src={thumb} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                  : <div className="w-full h-full flex items-center justify-center"><Play className="w-5 h-5 text-white/40" /></div>
                                }
                                <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/20 transition-colors">
                                  <div className="w-7 h-7 rounded-full bg-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow">
                                    <Play className="w-3.5 h-3.5 text-red-600 fill-red-600 ml-0.5" />
                                  </div>
                                </div>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-700 line-clamp-2 leading-snug" style={{ fontFamily: 'Prompt' }}>
                                  {video.name?.trim() || '(ไม่มีชื่อ)'}
                                </p>
                                <p className="text-xs text-gray-400 mt-1 truncate">{video.video_url}</p>
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
              )}

              {/* Folders view */}
              {tab === 'images' && view === 'folders' && (
                loadingFolders
                  ? <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
                  : filteredFolders.length === 0
                    ? (
                      <div className="text-center py-16 text-gray-400" style={{ fontFamily: 'Prompt' }}>
                        <FolderOpen className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p>ไม่พบโฟลเดอร์</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                        {filteredFolders.map(folder => (
                          <motion.div
                            key={folder.id}
                            className="group flex flex-col rounded-xl overflow-hidden border border-gray-200 hover:border-purple-400 hover:shadow-md transition-all bg-white cursor-pointer"
                            whileHover={{ y: -2, transition: { duration: 0.15 } }}
                            onClick={() => openFolder(folder)}
                          >
                            {/* Thumbnail */}
                            <div className="aspect-video bg-gray-100 overflow-hidden relative">
                              {folder.cover_image_url ? (
                                <img src={resolveImg(folder.cover_image_url)} alt={folder.name}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-purple-200">
                                  <FolderOpen className="w-8 h-8 text-purple-400" />
                                </div>
                              )}

                              {/* "เลือกทั้งโฟลเดอร์" button — multi mode only */}
                              {multiMode && (
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover:opacity-100">
                                  <button
                                    onClick={(e) => selectWholeFolder(folder, e)}
                                    disabled={loadingFolder === folder.id}
                                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-lg transition"
                                    style={{ background: 'linear-gradient(135deg,#533193,#7C3AED)', fontFamily: 'Prompt' }}
                                  >
                                    {loadingFolder === folder.id
                                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      : <LayoutGrid className="w-3.5 h-3.5" />
                                    }
                                    เลือกทั้งโฟลเดอร์
                                  </button>
                                </div>
                              )}
                            </div>

                            <div className="p-2.5 flex items-center justify-between">
                              <p className="text-xs font-medium text-gray-700 line-clamp-1 flex-1" style={{ fontFamily: 'Prompt' }}>
                                {folder.name}
                              </p>
                              <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0 ml-1" />
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    )
              )}

              {/* Images view */}
              {tab === 'images' && view === 'images' && (
                loadingImages
                  ? <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>
                  : filteredImages.length === 0
                    ? <div className="text-center py-16 text-gray-400" style={{ fontFamily: 'Prompt' }}><p>ไม่พบรูปภาพ</p></div>
                    : (
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {filteredImages.map(img => {
                          const thumbUrl = resolveImg(img.image_url || img.thumbnail_url);
                          const isChecked = checkedUrls.has(img.image_url);

                          return (
                            <motion.button
                              key={img.id}
                              onClick={() => multiMode ? toggleCheck(img.image_url) : handleSingleSelect(img.image_url)}
                              className={`relative group rounded-lg overflow-hidden aspect-square border-2 transition-all ${
                                isChecked
                                  ? 'border-purple-500 shadow-md'
                                  : 'border-transparent hover:border-purple-300'
                              }`}
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.97 }}
                            >
                              <img src={thumbUrl} alt={img.name || ''} className="w-full h-full object-cover" />

                              {/* Overlay */}
                              <div className={`absolute inset-0 transition-colors ${
                                isChecked ? 'bg-purple-600/25' : 'bg-black/0 group-hover:bg-black/15'
                              }`} />

                              {/* Checkbox indicator (multi mode) */}
                              {multiMode && (
                                <div className={`absolute top-1.5 right-1.5 transition-opacity ${
                                  isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                }`}>
                                  {isChecked
                                    ? <CheckCircle2 className="w-5 h-5 text-white drop-shadow" style={{ fill: '#7C3AED', color: 'white' }} />
                                    : <div className="w-5 h-5 rounded-full bg-white/80 border-2 border-gray-400" />
                                  }
                                </div>
                              )}

                              {/* Single-select checkmark */}
                              {!multiMode && (
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow">
                                    <CheckCircle2 className="w-5 h-5 text-purple-600" />
                                  </div>
                                </div>
                              )}
                            </motion.button>
                          );
                        })}
                      </div>
                    )
              )}
            </div>

            {/* ── Footer ── */}
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-3" style={{ fontFamily: 'Prompt' }}>
              <p className="text-xs text-gray-400">
                {tab === 'videos'
                  ? 'คลิกวิดีโอเพื่อเลือก'
                  : multiMode && view === 'images' && checkedUrls.size > 0
                    ? `เลือกแล้ว ${checkedUrls.size} รูป`
                    : multiMode
                      ? 'คลิกรูปเพื่อเลือก • hover โฟลเดอร์แล้วกด "เลือกทั้งโฟลเดอร์"'
                      : 'คลิกรูปเพื่อเลือก'
                }
              </p>
              <div className="flex items-center gap-2">
                <button onClick={onClose}
                  className="px-4 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition">
                  ปิด
                </button>
                {tab === 'images' && multiMode && view === 'images' && checkedUrls.size > 0 && (
                  <Button
                    onClick={confirmMultiSelect}
                    className="text-white text-sm px-4 py-1.5 h-auto"
                    style={{ background: 'linear-gradient(135deg,#533193,#7C3AED)' }}
                  >
                    เพิ่ม {checkedUrls.size} รูป
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
